import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '~/components/Layout';
import MessagingService from '~/api/services/messagingSettings';
import { useTranslation } from 'react-i18next';
import { Tab } from 'semantic-ui-react';
import { useForm, FormProvider } from 'react-hook-form';
import notify from '~/components/Notify';

//Tabs
import SmsVerification from './Tabs/SmsVerification';
import UserActivationResetPassword from './Tabs/UserActivationResetPassword';
import Notifications from './Tabs/Notifications';
import Newsletter from './Tabs/Newsletter';
import { StyledHeader, StyledTabPane, StyledMenuItem } from './style';
import InfoPopup from '~/components/InfoPopup';
import { handleErrors } from '~/utils/helpers';
import { useAuth } from '~/routes/components/AuthProvider';
import { FAT_PROTEIN_SLUG, MILK_FIXED_SLUG, MILK_MONEY_ACCOUNT_SLUG, MILK_STOCK_EXCHANGE } from '~/Constants';

const defaultErrorTabs = [
  {
    name: 'texts',
    index: 0,
    value: false
  },
  {
    name: ['newTradingSlotEmails', 'summaryEmails', 'completedSummaryEmails', 'toursImportErrorEmails'],
    index: 2,
    value: false
  }
]

const tradingSlotSlugs = [MILK_FIXED_SLUG, MILK_STOCK_EXCHANGE, FAT_PROTEIN_SLUG]

const Messaging: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { systemData: { modules } } = useAuth()

  const [messagingOptions, setMessagingOptions] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [tabIndex, setTabIndex] = useState(-1)

  const methods = useForm({
    mode: 'onSubmit'
  });
  const { formState } = methods
  const [errorTabs, setErrorTabs] = useState(defaultErrorTabs)

  const hasAnyTradingSlot = Boolean(modules.find(module => !!tradingSlotSlugs.find(tradingSlotSlug => tradingSlotSlug === module.slug)))
  const hasMilkMoney = modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG)

  useEffect(() => {
    fetchMessagingData();
  }, []);

  const createObjectsFromString = (stringArray) => stringArray.map((email: string) => ({ email: email }))

  async function fetchMessagingData() {
    setIsBusy(true);
    try {
      const responseMessagingData = await MessagingService.getMessagingOptionsData();
      setMessagingOptions(responseMessagingData.data);
      const responseMessagingEdit = await MessagingService.getMessagingEdit();
      responseMessagingData.data.languages.map((language) => {
        const index = responseMessagingEdit.data.texts[0].languages.findIndex(languageText => languageText.id === language.id)
        if (index < 0) {
          responseMessagingEdit.data.texts[0].languages.push({
            id: language.id,
            translation: '',
            order: language.order
          })
        } else {
          responseMessagingEdit.data.texts[0].languages[index].order = language.order
        }
      })
      //reverse languages by order
      responseMessagingEdit.data.texts[0]?.languages?.sort((a, b) => a.order - b.order)
      if (hasAnyTradingSlot) {
        // Notification mails
        responseMessagingEdit.data.summaryEmails = createObjectsFromString(responseMessagingEdit.data.summaryEmails)
        responseMessagingEdit.data.newTradingSlotEmails = createObjectsFromString(responseMessagingEdit.data.newTradingSlotEmails)
        responseMessagingEdit.data.completedSummaryEmails = createObjectsFromString(responseMessagingEdit.data.completedSummaryEmails)
      }
      if (hasMilkMoney) {
        responseMessagingEdit.data.toursImportErrorEmails = createObjectsFromString(responseMessagingEdit.data.toursImportErrorEmails)
        responseMessagingEdit.data.lastDeliveryDateDeactivatedEmails = createObjectsFromString(responseMessagingEdit.data.lastDeliveryDateDeactivatedEmails)
      }
      methods.reset(responseMessagingEdit.data);
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  useEffect(() => {
    if (Object.keys(formState.errors).length > 0) {
      if (formState.errors?.texts) {
        const tabIndexLang = formState.errors?.texts ? formState.errors?.texts[0]?.languages?.findIndex(translation => !!translation) : -1
        if (tabIndexLang >= 0) {
          setActiveIndex(0)
          setTabIndex(tabIndexLang)
        }
      } else {
        setActiveIndex(2)
      }
      notify(t('Please fill in all required fields'), 'error')
    }
  }, [formState.errors, formState.isSubmitted])

  const suffixOptions = messagingOptions?.settings.emailSuffix.map((item) => ({
    value: item,
    text: item
  }));

  function errorTab() {
    const errorTabsTemp = [...errorTabs]
    errorTabsTemp.map((errorTab, index) => {
      if (typeof (errorTab.name) === 'string') {
        errorTabsTemp[index].value = Object.keys(formState.errors).find(key => key === errorTab.name) !== undefined
      } else {
        const errorValue = errorTab.name.find(name => Object.keys(formState.errors).find(key => key === name) !== undefined)
        errorTabsTemp[index].value = errorValue !== undefined
      }
    })
    return errorTabsTemp
  }

  function getMenuItem(pane, index) {
    const errors = errorTab()
    const paneHasError = errors.find(pane => pane.index === index)

    return <StyledMenuItem
      key={pane.menuItem}
      className='tab-item'
    >
      <div className={`${paneHasError?.value ? 'error' : ''}`}>{pane.menuItem}</div>
    </StyledMenuItem>
  }

  function renderTabs() {
    const panes = [
      {
        menuItem: t('SMS 2FA'),
        component: <SmsVerification
          tabIndex={tabIndex}
          messagingOptions={messagingOptions}
        />,
        display: messagingOptions?.smsActive
      },
      {
        menuItem: t('User Activation / Reset password'),
        component: <UserActivationResetPassword />,
        display: true
      },
      {
        menuItem: t('Notifications'),
        component: <Notifications />,
        display: hasAnyTradingSlot || hasMilkMoney
      },
      {
        menuItem: t('Newsletter & Email'),
        component: <Newsletter suffixOptions={suffixOptions} />,
        display: messagingOptions?.isNewsletterEnabled
      }
    ];
    if (id) {
      const locationIndex = panes.findIndex((pane) => pane.menuItem === t('Location'));
      if (locationIndex >= 0) {
        panes.splice(locationIndex, 1);
      }
    }
    return (
      <>
        <Tab
          activeIndex={activeIndex}
          //@ts-ignore
          onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
          renderActiveOnly={false}
          menu={{ secondary: true, pointing: true }}
          panes={panes.map((pane, index) => {
            return (
              Boolean(pane.display) && {
                menuItem: getMenuItem(pane, index),
                pane: (
                  <StyledTabPane
                    //eslint-disable-next-line
                    children={pane.component}
                    active={index === activeIndex}
                    key={index}
                  />
                )
              }
            );
          })}
        />
      </>
    );
  }

  async function onSubmit(data: any) {
    // we delete prefix
    if (data.newsletterEmail.split('@')[0] === '') {
      data.newsletterEmail = `no-reply${data.newsletterEmail}`;
    }
    if (data.email.split('@')[0] === '') {
      data.email = `no-reply${data.email}`;
    }

    // Notification mails
    if (data?.summaryEmails) {
      data.summaryEmails = data.summaryEmails.map((summaryEmails) => summaryEmails.email);
    }
    if (data?.newTradingSlotEmails) {
      data.newTradingSlotEmails = data.newTradingSlotEmails.map(
        (newTradingSlotEmails) => newTradingSlotEmails.email
      );
    }
    if (data.completedSummaryEmails) {
      data.completedSummaryEmails = data.completedSummaryEmails.map(
        (completedSummaryEmails) => completedSummaryEmails.email
      );
    }
    if (data?.toursImportErrorEmails) {
      data.toursImportErrorEmails = data.toursImportErrorEmails.map(
        (toursImportErrorEmails) => toursImportErrorEmails.email
      );
    }
    if (data?.lastDeliveryDateDeactivatedEmails) {
      data.lastDeliveryDateDeactivatedEmails = data.lastDeliveryDateDeactivatedEmails.map(
        (lastDeliveryDateDeactivatedEmails) => lastDeliveryDateDeactivatedEmails.email
      );
    }

    setIsBusy(true);
    try {
      await MessagingService.getMessagingUpdate(data);
      notify(t('Messaging settings updated'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderTitle() {
    return <StyledHeader>
      <div className='title'>{t('Messaging settings')}</div>
      <InfoPopup
        content={t('Here you can customize all settings related to messaging (SMS, newsletters, notifications, etc.).')}
        styleIcon={{ cursor: 'pointer', fontSize: '1.4em', marginLeft: 5 }}
      />
    </StyledHeader>
  }

  return (
    <Layout
      isBusy={isBusy}
      segmented
      onSubmit={methods.handleSubmit(onSubmit)}
      title={renderTitle()}
      action={t('Update')}
    >
      {!isBusy && messagingOptions && Object.keys(methods.getValues()).length > 0 && (
        <FormProvider {...methods}>{renderTabs()}</FormProvider>
      )}
    </Layout>
  );
};

export default Messaging;
